import { computed, onMounted, onUnmounted, Ref, ref } from 'vue';
import { AdapterService } from '../adapter';
import {
  CreateFileFolderModel,
  FileImportProgressModel,
  RemoveFileModel,
  TaskFormDataModel,
  TaskModel,
  UpdateFileNameModel,
} from '../models';
import { TirPmNotificationEventBus, TirPmNotificationModel } from 'tir-components';
import { FileUploadCallbackFunctionType } from 'tir-pm-texteditor-file';
import { CurrentUser } from 'tir-pm-user-core';
import { TaskFileModel, UserModel } from '@/components/task-forms/models';
import { TaskCropImageModal, TaskCropImageModel } from '../..';
import {
  base64ToFile,
  checkIsImageFile,
  getBase64FromFile,
  getFileExtension,
} from '@/components/task-forms/utils';
import { FileFolderTypeEnum } from '@/enums';
import { AbstractFileModel, FileManager, FileModel, FileUploadStatusEnum, ImportVideoModel, VideoModel } from 'tir-pm-file-upload-manager';

type UseTaskFilesParams = {
  /**Текущая задача */
  currentTask: Ref<TaskModel | undefined>;
  /**Данные формы */
  formData: Ref<TaskFormDataModel>;
  /**Id проекта */
  projectId: number;
  /**Id очереди */
  queueId: string;
};

export const useTaskFiles = (params: UseTaskFilesParams) => {
  const adapter = new AdapterService();

  /**Функция для установки слота под файл в описании */
  let pasteFileSlotToDescription: (slotId: number) => void;
  /**ID файла при редактировании изображения */
  let editImageFileId: number;
  /**Ссылка на модальное окно кропа изображения */
  const $cropImageModal = ref<InstanceType<typeof TaskCropImageModal>>();

  /**Загрузка при прикреплении файлов */
  const isLoadingAttachFiles = ref(false);
  /**Загрузка при обновлении названия файла */
  const filesLoadingEditName = ref(new Array<number>());
  /**Загрузка в модальном окне кропа */
  const isLoadingCropModal = ref(false);

  /**Загружаемыые файлы */
  const uploadingFiles = computed(() => {
    const uploadFilesForCurrentTask = FileManager.ActiveUploads.value.filter(
      (file: VideoModel | FileModel) => {
        return (
          file.Location &&
          params.formData.value.FolderId &&
          file.Location.FolderId === params.formData.value.FolderId &&
          file.Status === FileUploadStatusEnum.Uploading &&
          (file.isVideoModel() || file.isFileModel())
        );
      },
    );

    const files = new Array<TaskFileModel>();

    files.push(
      ...uploadFilesForCurrentTask.map((file) => {
        return new TaskFileModel({
          Id: file.FileId,
          Url: file.Url,
          VideoPreviewUrl: file.isVideoModel() ? file.ThumbnailUrl : undefined,
          Name: file.Name,
          Size: file.Size,
          DateUpload: file.DateCreate,
          User: new UserModel({
            Id: CurrentUser.Id,
            FirstName: CurrentUser.FirstName,
            LastName: CurrentUser.LastName,
            MiddleName: CurrentUser.MiddleName,
            AvatarColor: CurrentUser.AvatarColor,
            ImageUrl: CurrentUser.Foto,
          }),
          UploadProgress: file.Progress,
          IsProccessing: file.Status === FileUploadStatusEnum.Processing,
          IsSendingLastChunk:
            (file.isFileModel() || file.isVideoModel()) && file.IsSendingLastChunk,
        });
      }),
    );

    return files;
  });
  /**Существующие имена файлов */
  const existFileNames = computed(() => {
    return [...params.formData.value.Files, ...uploadingFiles.value].map((f) => f.Name);
  });

  /**При закрытии модального окна с кропом изображения */
  const onCloseCropImageModal = () => {
    pasteFileSlotToDescription = undefined;
    editImageFileId = undefined;
  };

  /**
   * Создать папку под файлы для задачи
   * @returns
   */
  const createFilesFolderForTask = async () => {
    const resp = await adapter.createFolderWithoutEntity(
      new CreateFileFolderModel({
        ProjectId: params.projectId,
        Type: FileFolderTypeEnum.Task,
      }),
    );
    if (!resp.IsSuccess || !resp.Value) return false;
    params.formData.value.FolderId = resp.Value;
    return true;
  };

  /**Прикрепить файлы */
  const attachFiles = async (files: Array<File>) => {
    if (!params.currentTask.value?.Id || !files.length) return;

    const [file] = files;

    // Base64 файла
    let base64Image: string;

    // Если загружается 1 файл и это изображение
    if (
      files.length === 1 &&
      checkIsImageFile(file) &&
      !['svg', 'gif'].includes(getFileExtension(file.name))
    ) {
      base64Image = await getBase64FromFile(file);
      if (!base64Image) return;
    } else {
      isLoadingAttachFiles.value = true;
    }

    if (base64Image) {
      // Открываем модальное окно с кропом изображения
      openCropModal(
        new TaskCropImageModel({
          Base64OrUrl: base64Image,
          FileName: file.name,
          CompressFile: true,
        }),
        false,
      );

      return;
    }

    await FileManager.uploadBatch({
      Items: files.map((file) => ({ File: file, CompressSize: 5 })),
      Location: {
        FolderId: params.formData.value.FolderId,
        TaskId: params.currentTask.value.Id,
        QueueTaskId: params.currentTask.value.QueueTaskId,
      },
    });
    isLoadingAttachFiles.value = false;
  };

  /**
   * Вставить файл из описания
   * @param file Файл
   * @param pasteSlotElement Функция для вставки в редактор
   * @returns
   */
  const attachFileFromDescription: FileUploadCallbackFunctionType = async (
    file: File,
    pasteSlotElement: (slotId: string | number) => void,
  ) => {
    // Является ли файл изображением
    const isImage = checkIsImageFile(file);
    // Base64 файла

    let base64Image: string;
    if (isImage && !['svg', 'gif'].includes(getFileExtension(file.name))) {
      base64Image = await getBase64FromFile(file);
      if (!base64Image) return;
      // Запоминаем коллбек для вставки слота под файл в описание
      pasteFileSlotToDescription = pasteSlotElement;
    } else {
      isLoadingAttachFiles.value = true;
    }

    // Если файл добавляется в режиме создания задачи и отсутствует идентификатор папки с файлами
    if (!params.currentTask.value?.Id && !params.formData.value.FolderId) {
      if (!(await createFilesFolderForTask())) return;
    }

    if (base64Image) {
      // Открываем модальное окно с кропом изображения
      openCropModal(
        new TaskCropImageModel({
          Base64OrUrl: base64Image,
          FileName: file.name,
          CompressFile: true,
        }),
        false,
      );

      return;
    }

    const resp = await FileManager.upload({
      Item: { File: file },
      Location: {
        FolderId: params.formData.value.FolderId,
        TaskId: params.currentTask.value?.Id,
        QueueTaskId: params.queueId,
      },
    });

    if (resp.IsSuccess) {
      pasteSlotElement(resp.Value.FileId);
    }
    isLoadingAttachFiles.value = false;
  };

  /**
   * Открыть окно редактирования изображения
   * @param fileId ID файла
   */
  const openEditImage = (fileId: number) => {
    // Файл на редактирование
    const file = params.formData.value.Files.find((file) => file.Id === fileId);

    if (!file) return;

    editImageFileId = fileId;

    openCropModal(
      new TaskCropImageModel({
        FileName: file.Name,
        Base64OrUrl: file.Url,
        CompressFile: false,
      }),
      true,
    );
  };

  /**
   * Сохранить изображение после кропа
   * @param data Данные изображения
   */
  const saveImageFromCrop = async (data: TaskCropImageModel) => {
    const pasteFileSlot = pasteFileSlotToDescription;
    const fileId = editImageFileId;
    pasteFileSlotToDescription = undefined;
    editImageFileId = undefined;

    const file = base64ToFile(data.Base64OrUrl, data.FileName);
    isLoadingCropModal.value = true;

    if (fileId) {
      params.formData.value.Files = params.formData.value.Files.filter(
        (file) => file.Id !== fileId,
      );
    }

    const resp = await FileManager.upload({
      Item: { File: file, FileId: fileId, CompressSize: data.CompressFile ? 5 : undefined },
      Location: {
        FolderId: params.formData.value.FolderId,
        TaskId: params.currentTask.value?.Id,
        QueueTaskId: params.queueId,
      },
    });
    isLoadingCropModal.value = false;
    $cropImageModal.value?.close();

    if (resp.IsSuccess && pasteFileSlot) {
      pasteFileSlot(resp.Value.FileId);
    }
  };

  /**Обновить название файла */
  const updateFileName = async (fileId: number, fileName: string) => {
    if (!fileName?.trim() || !params.currentTask.value?.Id) return;
    const file = params.currentTask.value.Files.find((file) => file.Id === fileId);
    if (!file) return;
    filesLoadingEditName.value.push(fileId);

    const resp = await adapter.updateFileName(
      new UpdateFileNameModel({
        TaskId: params.currentTask.value?.Id,
        FileId: fileId,
        Name: `${fileName}.${getFileExtension(file.Name)}`,
      }),
    );
    if (resp.IsSuccess && resp.Value) {
      file.Name = resp.Value.FileName;
      file.Url = resp.Value.Url;

      TirPmNotificationEventBus.emit(
        'add',
        new TirPmNotificationModel({
          title: 'Файл был успешно переименован',
          type: 'success',
          group: 'general',
          life: 2000,
          closable: true,
        }),
      );
    } else {
      TirPmNotificationEventBus.emit(
        'add',
        new TirPmNotificationModel({
          title: 'Произошла ошибка при переименовании файла',
          type: 'error',
          group: 'general',
          life: 2000,
          closable: true,
        }),
      );
    }
    filesLoadingEditName.value = filesLoadingEditName.value.filter((_fileId) => _fileId !== fileId);
  };

  /**Удалить файл */
  const removeFile = async (fileId: number, fromDescription: boolean) => {
    if (fromDescription) {
      removeFileFromDescription(fileId);
      return;
    }
    if (!params.currentTask.value?.Id) return;
    const resp = await adapter.removeFile(
      new RemoveFileModel({
        TaskId: params.currentTask.value?.Id,
        FileId: fileId,
      }),
    );
    if (resp.IsSuccess) {
      params.currentTask.value.Files = params.currentTask.value.Files.filter(
        (file) => file.Id !== fileId,
      );
      params.formData.value.Files = [...params.currentTask.value.Files];
      TirPmNotificationEventBus.emit(
        'add',
        new TirPmNotificationModel({
          title: 'Файл был успешно удален',
          type: 'success',
          group: 'general',
          life: 2000,
          closable: true,
        }),
      );
    } else {
      TirPmNotificationEventBus.emit(
        'add',
        new TirPmNotificationModel({
          title: 'Произошла ошибка при удалении файла',
          type: 'error',
          group: 'general',
          life: 2000,
          closable: true,
        }),
      );
    }
  };

  /**
   * Удалить файл из описания
   * @param fileId ID фалйа
   * @returns
   */
  const removeFileFromDescription = async (fileId: number) => {
    if (!params.formData.value?.Description?.trim()) return;

    const parser = new DOMParser();
    const doc = parser.parseFromString(params.formData.value.Description, 'text/html');

    // Ищем все элементы с атрибутом data-widget-id
    const elements = doc.querySelectorAll(`[data-widget-id='${fileId}']`);
    elements.forEach((el) => {
      el.remove();
    });

    params.formData.value.Description = doc.body.innerHTML;
  };

  /**
   * * При начале обработки файла
   * @param fileOrVideo
   * @returns
   */
  const onStartProccessingFile = (fileOrVideo: VideoModel | ImportVideoModel) => {
    if (
      !params.formData.value.FolderId ||
      params.formData.value.Files.some((_file) => _file.Id === fileOrVideo.FileId)
    )
      return;
    if (fileOrVideo.Location.FolderId !== params.formData.value.FolderId) return;

    params.formData.value.Files.push(
      new TaskFileModel({
        Id: fileOrVideo.FileId,
        Url: fileOrVideo.Url,
        VideoPreviewUrl: fileOrVideo.isVideoModel() ? fileOrVideo.ThumbnailUrl : undefined,
        Name: fileOrVideo.Name,
        Size: fileOrVideo.Size,
        DateUpload: fileOrVideo.DateCreate,
        IsProccessing: true,
        UploadProgress: 100,
        User: new UserModel({
          Id: CurrentUser.Id,
          FirstName: CurrentUser.FirstName,
          LastName: CurrentUser.LastName,
          MiddleName: CurrentUser.MiddleName,
          AvatarColor: CurrentUser.AvatarColor,
          ImageUrl: CurrentUser.Foto,
        }),
      }),
    );
  };

  /**
   * * Обработчик успешного добавления файла из FileManager
   * @param file
   */
  const onCompleteAddFileFromFileManager = (file: AbstractFileModel) => {
    if (
      file.Location.FolderId !== params.formData.value.FolderId ||
      params.formData.value.Files.some((_file) => _file.Id === file.FileId)
    )
      return;

    params.formData.value.Files.push(
      new TaskFileModel({
        Id: file.FileId,
        Url: file.Url,
        VideoPreviewUrl: file.isVideoModel() ? file.ThumbnailUrl : undefined,
        Name: file.Name,
        Size: file.Size,
        DateUpload: file.DateCreate,
        User: new UserModel({
          Id: CurrentUser.Id,
          FirstName: CurrentUser.FirstName,
          LastName: CurrentUser.LastName,
          MiddleName: CurrentUser.MiddleName,
          AvatarColor: CurrentUser.AvatarColor,
          ImageUrl: CurrentUser.Foto,
        }),
      }),
    );
  };

  /**
   * * Обработчик успешного добавления файла
   * @param fileId Id файла
   */
  const onCompleteAddFile = (fileId: number) => {
    const file = params.formData.value.Files.find((file) => file.Id === fileId);
    if (file) {
      file.UploadProgress = undefined;
      file.IsProccessing = false;
    }
  };

  /**
   * Обработчик события прогресса импорта файла
   * @param event
   * @returns
   */
  const onProgressImportFile = (event: FileImportProgressModel) => {
    const file = params.formData.value.Files.find((file) => file.Id === event.FileId);
    if (!file) return;
    file.IsProccessing = event.Progress === 100;
    file.UploadProgress = file.UploadProgress;
    file.UploadId = file.UploadId;
  };

  /**Сброс данных */
  const resetFilesData = () => {
    isLoadingAttachFiles.value = false;
    filesLoadingEditName.value = new Array<number>();
  };

  /**Открытие окна обрезки */
  const openCropModal = (image: TaskCropImageModel, isEdit: boolean) => {
    if (existFileNames.value.includes(image.FileName)) {
      image.FileName = getUniqueFilename(image.FileName, existFileNames.value);
    }
    $cropImageModal.value?.open(image, isEdit);
  };

  /**Получение уникального имени файла */
  function getUniqueFilename(filename: string, existingFiles: string[]) {
    let match = filename.match(/^(.*?)(\((\d+)\))?(\.[^.]*)?$/);
    let baseName = match?.[1];
    let existingNumber = match?.[3] ? parseInt(match[3], 10) : 0;
    let ext = match?.[4] || '';
    let counter = existingNumber ? existingNumber + 1 : 1;
    let newFilename = filename;

    while (existingFiles.includes(newFilename)) {
      newFilename = `${baseName}(${counter})${ext}`;
      counter++;
    }

    return newFilename;
  }

  onMounted(() => {
    FileManager.StartProcessingEvent.on(onStartProccessingFile);
    FileManager.CompleteEvent.on(onCompleteAddFileFromFileManager);
    adapter.OnCompleteAddFileEvent.on(onCompleteAddFile);
    adapter.FileImportProgressEvent.on(onProgressImportFile);
  });

  onUnmounted(() => {
    FileManager.StartProcessingEvent.removeListener(onStartProccessingFile);
    FileManager.CompleteEvent.removeListener(onCompleteAddFileFromFileManager);
    adapter.OnCompleteAddFileEvent.removeListener(onCompleteAddFile);
    adapter.FileImportProgressEvent.removeListener(onProgressImportFile);
  });

  return {
    /**Открыть окно редактирования изображения */
    openEditImage,
    /**Загрузка в модальном окне кропа */
    isLoadingCropModal,
    /**Сохранить изображение после кропа */
    saveImageFromCrop,
    /**При закрытии модального окна с кропом изображения */
    onCloseCropImageModal,
    /**Ссылка на модальное окно кропа изображения */
    $cropImageModal,
    /**Загрузка при прикреплении файлов */
    isLoadingAttachFiles,
    /**Загрузка при обновлении названия файла */
    filesLoadingEditName,
    /**Загружаемые файлы */
    uploadingFiles,
    /**Прикрепить файлы */
    attachFiles,
    /**Вставить файл из описания */
    attachFileFromDescription,
    /**Обновить название файла */
    updateFileName,
    /**Удалить файл */
    removeFile,
    /**Сброс данных */
    resetFilesData,
    /**Удалить файл из описания */
    removeFileFromDescription,
    /**Существующие имена файлов */
    existFileNames,
  };
};
